import React from "react"
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form"

import { FreeResponseQuestion } from "./free-response-question"
import { DateQuestion } from "./date-question"
import { CheckboxGroupQuestion } from "./checkbox-group-question"
import { RadioGroupQuestion } from "./radio-group-question"
import { DropdownQuestion } from "./dropdown-question"
import { PdfUploadQuestion } from "./pdf-upload-question"
import { BranchingQuestion } from "./branching-question"
import { PlaceOrderFormData, Question } from "types"

interface QuestionRendererProps {
  question: Question
  register: UseFormRegister<PlaceOrderFormData>
  errors: FieldErrors<PlaceOrderFormData>
  watch: UseFormWatch<PlaceOrderFormData>
  setValue: UseFormSetValue<PlaceOrderFormData>
  ticketId: number
  isFirstTicket: boolean
  isSameAsContact: boolean
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  register,
  errors,
  watch,
  setValue,
  ticketId,
  isFirstTicket,
  isSameAsContact,
}) => {
  const commonProps = {
    question,
    register,
    errors,
    watch,
    setValue,
    ticketId,
  }

  const freeResponseProps = {
    ...commonProps,
    isFirstTicket,
    isSameAsContact,
  }

  const checkboxProps = {
    question,
    errors,
    ticketId,
  }

  const radioProps = {
    question,
    errors,
    watch,
    setValue,
    ticketId,
  }

  // Check if this is a branching question (has choices with question_branch)
  const hasBranching = question.choices?.some(
    (choice) => choice.question_branch && choice.question_branch.length > 0
  )

  if (hasBranching) {
    return <BranchingQuestion {...commonProps} />
  }

  switch (question.question_type) {
    case "free-response":
      return <FreeResponseQuestion {...freeResponseProps} />

    case "date":
      return <DateQuestion {...commonProps} />

    case "multiple-choice":
      if (question.display_type === "drop-down-display")
        return <DropdownQuestion {...commonProps} />

      if (question.selection_type === "multiple-answer")
        return <CheckboxGroupQuestion {...checkboxProps} />

      return <RadioGroupQuestion {...radioProps} />

    case "pdf":
      return <PdfUploadQuestion {...commonProps} />

    default:
      return (
        <div className="p-6 text-red-700 bg-red-50 rounded-lg border border-red-300">
          <p className="text-sm">
            Unsupported question type: {question.question_type}
          </p>
        </div>
      )
  }
}

export { QuestionRenderer }
